/* [ASB pdp] Sticky "Choose options" bar on the compact product page
   Custom file — not part of Dawn 15.4.0.
   Built by: Codex (2026-10)
   What it does: shows the bar while the buy buttons and the option choices are off screen, and scrolls to the first
   visible option group. [ASB pdp] 2026-10-31 (Claude): the bar also hides while any option choice (size chips, APO
   choices) is on screen — on phones it sat over the Spray / Body Oil and size buttons. Later the same day: the
   theme's own Spray / Body Oil choice (.asb-format-choice) counts as an option group too.
   2026-11-03 (Claude, review): on Size products where nothing else needs choosing (no APO rows, scent pickers or text
   fields), Add to cart is ready and the shopper has scrolled past it, the bar shows the chosen size and price and its
   own Add to cart, which presses the page's Add to cart (same checks, cart pop-up, no double add). Otherwise it stays
   "Choose options", as before.
   Settings/data: main-product setting enable_pdp_conversion.
   Used by: sections/main-product.liquid (<asb-pdp-nav>). */
if (!customElements.get('asb-pdp-nav')) {
  customElements.define('asb-pdp-nav', class extends HTMLElement {
    connectedCallback() {
      this.product = this.closest('product-info');
      this.link = this.querySelector('a');
      this.add = this.querySelector('button');
      this.choice = this.querySelector('.asb-pdp-nav__choice');
      this.target = this.product.querySelector('.asb-pdp-options');
      this.buy = this.product.querySelector('.product-form__buttons');
      if (!this.target || !this.buy) return;
      this.onClick = (event) => {
        event.preventDefault();
        // .asb-format-choice: Spray / Body Oil choice on products with a native body-oil variant (2026-10-31).
        const group = [...this.target.querySelectorAll('.asb-format-choice, .mw-option:not(.asb-apo-size-off), variant-selects, .product-form__quantity')]
          .find(element => element.getBoundingClientRect().height > 0) || this.target;
        const input = group.querySelector('input:checked:not(:disabled), select:not(:disabled), input:not(:disabled)');
        (input || this.target).focus({ preventScroll: true });
        group.scrollIntoView({ block: 'start', behavior: 'instant' });
      };
      this.onAdd = () => {
        const button = this.addButton();
        if (button && !button.disabled && button.getAttribute('aria-disabled') !== 'true') button.click();
      };
      this.onFocus = () => this.update();
      // APO and Dawn rebuild the option markup, so the choices are measured on scroll instead of observed.
      this.onScroll = () => {
        if (this.scrollQueued) return;
        this.scrollQueued = true;
        requestAnimationFrame(() => {
          this.scrollQueued = false;
          this.update();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.link.addEventListener('click', this.onClick);
      this.add?.addEventListener('click', this.onAdd);
      document.addEventListener('focusin', this.onFocus);
      document.addEventListener('focusout', this.onFocus);
      this.observer = new IntersectionObserver(([entry]) => {
        this.buyVisible = entry.isIntersecting;
        this.update();
      }, { rootMargin: '-100px 0px -80px 0px' });
      this.observer.observe(this.buy);
      const footer = document.querySelector('footer');
      if (footer) {
        this.footerObserver = new IntersectionObserver(([entry]) => {
          this.footerVisible = entry.isIntersecting;
          this.update();
        });
        this.footerObserver.observe(footer);
      }
    }
    optionsOnScreen() {
      const choices = this.product.querySelectorAll('.asb-format-choice, variant-selects, .mw-option:not(.asb-apo-size-off)');
      return [...choices].some((element) => {
        const box = element.getBoundingClientRect();
        return box.height > 0 && box.bottom > 0 && box.top < window.innerHeight;
      });
    }
    // The page's own Add to cart (APO can add a second one; the visible one counts).
    addButton() {
      return [...this.buy.querySelectorAll('button[name="add"]')].find((button) => button.getClientRects().length > 0);
    }
    // Text for "<size> · <price>" when the shopper only has to press Add to cart, else '' (keep "Choose options").
    readyChoice() {
      if (!this.add || !this.choice || !this.product.classList.contains('asb-native-sizes')) return '';
      const button = this.addButton();
      if (!button || button.disabled) return '';
      // Only after the shopper has scrolled past the sizes and Add to cart; above them (page top) it stays "Choose options".
      if (this.buy.getBoundingClientRect().bottom > 0) return '';
      // Fields that go into the order (gift set scents, engraving, APO choices), not the share-link box.
      const form = button.form;
      const extra = [...this.target.querySelectorAll('input:not([type="hidden"]), select, textarea')].some((field) =>
        field.form === form && !field.closest('variant-selects, .product-form__quantity') && field.getClientRects().length > 0);
      if (extra || this.product.querySelector('.mw-option:not(.asb-apo-size-off)')?.getClientRects().length) return '';
      const sizes = [...this.product.querySelectorAll('variant-selects input[type="radio"]:checked')].map((input) => input.value);
      const price = [...this.product.querySelectorAll('[id^="price-"] .price-item--sale, [id^="price-"] .price-item--regular')]
        .find((item) => item.getClientRects().length > 0)?.textContent.trim();
      return sizes.length && price ? `${sizes.join(' / ')} · ${price}` : '';
    }
    update() {
      const editing = document.activeElement?.matches('input:not([type="radio"]):not([type="checkbox"]), textarea, select, [contenteditable="true"]');
      const show = !this.buyVisible && !this.footerVisible && !editing && !this.optionsOnScreen();
      if (show) {
        const choice = this.readyChoice();
        if (this.choice && this.choice.textContent !== choice) this.choice.textContent = choice;
        if (this.choice) this.choice.hidden = !choice;
        if (this.add) this.add.hidden = !choice;
        this.link.hidden = Boolean(choice);
        this.toggleAttribute('data-ready', Boolean(choice));
      }
      if (this.hasAttribute('data-visible') !== show) this.toggleAttribute('data-visible', show);
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.footerObserver?.disconnect();
      this.link?.removeEventListener('click', this.onClick);
      this.add?.removeEventListener('click', this.onAdd);
      document.removeEventListener('focusin', this.onFocus);
      document.removeEventListener('focusout', this.onFocus);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }
  });
}
